import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import pdfService from "../services/pdfService";
import whatsappApi from "../services/api";
import { usePermissions } from "../utils/permissions";
import PermissionGate from "../componets/PermissionGate";
import BottomNavbar from "../componets/BottomNavbar";

const PDFManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const { activeRoles } = useSelector((state) => state.role);

  usePermissions();

  // Check permission directly from Redux state
  const canReadPDFs = useMemo(() => {
    if (user?.role === "admin") return true;
    const userRole = activeRoles.find((role) => role.name === user?.role);
    if (!userRole) return false;
    const permission = userRole.permissions.find((p) => p.resource === "contacts");
    return permission && permission.actions.includes("read");
  }, [user?.role, activeRoles]);

  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePdfId, setDeletePdfId] = useState(null);
  const [whatsappModal, setWhatsappModal] = useState({ open: false, pdf: null, phone: "" });
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [pdfPreviewModal, setPdfPreviewModal] = useState({
    open: false,
    pdf: null,
    blobUrl: null,
    blob: null,
    errorMessage: null,
  });

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!canReadPDFs) {
      navigate("/my-business");
      return;
    }

    loadPDFs();
  }, [dispatch, isAuthenticated, navigate, canReadPDFs]);

  const loadPDFs = async () => {
    setLoading(true);
    try {
      const response = await pdfService.getAllPDFs();
      setPdfs(response.pdfs || []);
    } catch (error) {
      console.error("Error loading PDFs:", error);
      toast.error(error.message || "Failed to load PDFs");
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = async (pdf) => {
    if (!pdf?._id) {
      toast.error("PDF not available");
      return;
    }
    setPdfPreviewModal({ open: true, pdf, blobUrl: null, blob: null, errorMessage: null });
    let blob = null;
    try {
      blob = await pdfService.getPDFBlob(pdf._id);
      const blobUrl = blob ? URL.createObjectURL(blob) : null;
      setPdfPreviewModal((prev) => ({ ...prev, blobUrl, blob, errorMessage: null }));
    } catch (err) {
      const status = err?.status ?? err?.response?.status;
      const msg = err?.message || "";
      const is404 = status === 404 || String(msg).toLowerCase().includes("not found");
      setPdfPreviewModal((prev) => ({
        ...prev,
        errorMessage: is404 ? "PDF file not found on server" : msg || "Failed to load PDF",
      }));
    }
  };

  const closePdfPreviewModal = () => {
    if (pdfPreviewModal.blobUrl) URL.revokeObjectURL(pdfPreviewModal.blobUrl);
    setPdfPreviewModal({ open: false, pdf: null, blobUrl: null, blob: null, errorMessage: null });
  };

  const handleDownloadPdfFromModal = () => {
    if (!pdfPreviewModal.blob || !pdfPreviewModal.pdf) return;
    const name = (pdfPreviewModal.pdf.fileName || "document").replace(/[^a-zA-Z0-9._-]/g, "_") + ".pdf";
    const a = document.createElement("a");
    a.href = URL.createObjectURL(pdfPreviewModal.blob);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleOpenPdfInNewTab = () => {
    if (!pdfPreviewModal.blobUrl) return;
    window.open(pdfPreviewModal.blobUrl, "_blank", "noopener");
  };

  const handleDeleteClick = (pdfId) => {
    setDeletePdfId(pdfId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletePdfId) return;

    try {
      await pdfService.deletePDF(deletePdfId);
      toast.success("PDF deleted successfully");
      setIsDeleteModalOpen(false);
      setDeletePdfId(null);
      loadPDFs();
    } catch (error) {
      console.error("Error deleting PDF:", error);
      toast.error(error.message || "Failed to delete PDF");
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeletePdfId(null);
  };

  const openWhatsappModal = (pdf) => {
    if (!pdf?.fileUrl) {
      toast.error("PDF file URL not available.");
      return;
    }
    const phone = (pdf.contact?.contactNumber || "").replace(/\D/g, "").slice(-10);
    setWhatsappModal({ open: true, pdf, phone });
  };

  const closeWhatsappModal = () => {
    setWhatsappModal({ open: false, pdf: null, phone: "" });
  };

  const handleSendWhatsAppPdf = async () => {
    const { pdf, phone } = whatsappModal;
    if (!pdf?.fileUrl) {
      toast.error("No PDF to send.");
      return;
    }
    const num = (phone || "").replace(/\D/g, "").slice(-10);
    if (num.length !== 10) {
      toast.error("Enter a valid 10-digit phone number.");
      return;
    }
    setSendingWhatsApp(true);
    try {
      await whatsappApi.post("/wp/send", {
        number: num,
        filePath: pdf.fileUrl.replace(/^\//, ""),
        message: pdf.fileName || "",
      });
      toast.success("PDF sent to WhatsApp.");
      closeWhatsappModal();
    } catch (err) {
      toast.error(err?.message || "Failed to send to WhatsApp.");
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Filter PDFs based on search term
  const filteredPDFs = useMemo(() => {
    if (!searchTerm.trim()) return pdfs;

    const searchLower = searchTerm.toLowerCase();
    return pdfs.filter(
      (pdf) =>
        pdf.fileName?.toLowerCase().includes(searchLower) ||
        pdf.contact?.name?.toLowerCase().includes(searchLower) ||
        pdf.contact?.contactNumber?.includes(searchLower) ||
        pdf.description?.toLowerCase().includes(searchLower)
    );
  }, [pdfs, searchTerm]);

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const styles = {
    container: {
      minHeight: "100vh",
      backgroundColor: "#f0f2f5",
      paddingBottom: isMobile ? "90px" : "100px",
    },
    header: {
      backgroundColor: "#ffffff",
      padding: isMobile ? "16px 16px 12px" : "20px 24px 16px",
      borderBottom: "1px solid #e9edef",
      position: "sticky",
      top: 0,
      zIndex: 100,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
    headerTitle: {
      fontSize: isMobile ? "24px" : "28px",
      fontWeight: "600",
      color: "#111b21",
      marginBottom: "8px",
      letterSpacing: "-0.01em",
    },
    headerSubtitle: {
      fontSize: "14px",
      color: "#667781",
      fontWeight: "400",
    },
    searchContainer: {
      padding: isMobile ? "12px 16px" : "16px 24px",
      backgroundColor: "#ffffff",
      borderBottom: "1px solid #e9edef",
    },
    searchInput: {
      width: "100%",
      padding: isMobile ? "10px 16px" : "12px 20px",
      fontSize: "15px",
      border: "1px solid #e9edef",
      borderRadius: "8px",
      backgroundColor: "#f0f2f5",
      outline: "none",
      transition: "border-color 0.2s",
    },
    content: {
      padding: isMobile ? "16px" : "24px",
    },
    pdfsList: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(350px, 1fr))",
      gap: isMobile ? "12px" : "16px",
    },
    pdfCard: {
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      padding: isMobile ? "16px" : "20px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      transition: "box-shadow 0.2s, transform 0.2s",
      cursor: "pointer",
      border: "1px solid #e9edef",
    },
    pdfCardHover: {
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
      transform: "translateY(-2px)",
    },
    pdfHeader: {
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      marginBottom: "12px",
    },
    pdfIcon: {
      fontSize: "32px",
      flexShrink: 0,
    },
    pdfInfo: {
      flex: 1,
      minWidth: 0,
    },
    pdfFileName: {
      fontSize: "16px",
      fontWeight: "500",
      color: "#111b21",
      marginBottom: "4px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      lineHeight: "22px",
    },
    pdfMeta: {
      fontSize: "13px",
      color: "#667781",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    pdfContact: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    pdfDate: {
      fontSize: "12px",
      color: "#8696a0",
    },
    pdfSize: {
      fontSize: "12px",
      color: "#8696a0",
    },
    pdfDescription: {
      fontSize: "13px",
      color: "#667781",
      marginTop: "8px",
      paddingTop: "8px",
      borderTop: "1px solid #f0f2f5",
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
    },
    pdfActions: {
      display: "flex",
      gap: "8px",
      marginTop: "12px",
      paddingTop: "12px",
      borderTop: "1px solid #f0f2f5",
    },
    actionButton: {
      flex: 1,
      padding: isMobile ? "8px 12px" : "10px 16px",
      fontSize: "13px",
      fontWeight: "500",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      transition: "background-color 0.2s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
    },
    viewButton: {
      backgroundColor: "#25d366",
      color: "#ffffff",
    },
    deleteButton: {
      backgroundColor: "#f15c6d",
      color: "#ffffff",
    },
    emptyState: {
      textAlign: "center",
      padding: isMobile ? "60px 20px" : "80px 40px",
      color: "#667781",
    },
    emptyStateIcon: {
      fontSize: "64px",
      marginBottom: "16px",
    },
    emptyStateText: {
      fontSize: "16px",
      fontWeight: "400",
      marginBottom: "8px",
    },
    emptyStateSubtext: {
      fontSize: "14px",
      color: "#8696a0",
    },
    loadingState: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#667781",
      fontSize: "16px",
    },
    // Modal styles
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1002,
      padding: isMobile ? "16px" : "24px",
    },
    modalContent: {
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      padding: isMobile ? "24px 20px" : "32px 24px",
      maxWidth: "400px",
      width: "100%",
      boxShadow: "0 8px 32px rgba(0,0,0,0.24)",
    },
    modalTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#111b21",
      marginBottom: "12px",
    },
    modalMessage: {
      fontSize: "15px",
      color: "#667781",
      marginBottom: "24px",
      lineHeight: "22px",
    },
    modalActions: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
    },
    modalButton: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      transition: "background-color 0.2s",
    },
    modalCancelButton: {
      backgroundColor: "#f0f2f5",
      color: "#111b21",
    },
    modalConfirmButton: {
      backgroundColor: "#f15c6d",
      color: "#ffffff",
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>PDF Files</h1>
        <p style={styles.headerSubtitle}>
          {loading ? "Loading..." : `${filteredPDFs.length} PDF${filteredPDFs.length !== 1 ? "s" : ""} found`}
        </p>
      </div>

      {/* Search */}
      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search by filename, contact name, or number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Content */}
      <div style={styles.content}>
        {loading ? (
          <div style={styles.loadingState}>Loading PDFs...</div>
        ) : filteredPDFs.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyStateIcon}>📄</div>
            <div style={styles.emptyStateText}>
              {searchTerm ? "No PDFs found matching your search" : "No PDFs found"}
            </div>
            <div style={styles.emptyStateSubtext}>
              {searchTerm
                ? "Try adjusting your search terms"
                : "PDFs will appear here when they are uploaded"}
            </div>
          </div>
        ) : (
          <div style={styles.pdfsList}>
            {filteredPDFs.map((pdf) => (
              <div
                key={pdf._id}
                style={styles.pdfCard}
                onMouseEnter={(e) => {
                  Object.assign(e.currentTarget.style, styles.pdfCardHover);
                }}
                onMouseLeave={(e) => {
                  Object.assign(e.currentTarget.style, {});
                }}
              >
                <div style={styles.pdfHeader}>
                  <div style={styles.pdfIcon}>📄</div>
                  <div style={styles.pdfInfo}>
                    <div style={styles.pdfFileName} title={pdf.fileName}>
                      {pdf.fileName || "Unnamed PDF"}
                    </div>
                    <div style={styles.pdfMeta}>
                      {pdf.contact && (
                        <div style={styles.pdfContact}>
                          <span>👤</span>
                          <span>{pdf.contact.name || "Unknown Contact"}</span>
                          {pdf.contact.contactNumber && (
                            <span style={{ marginLeft: "8px" }}>({pdf.contact.contactNumber})</span>
                          )}
                        </div>
                      )}
                      {pdf.createdAt && (
                        <div style={styles.pdfDate}>📅 {formatDate(pdf.createdAt)}</div>
                      )}
                      {pdf.fileSize && (
                        <div style={styles.pdfSize}>💾 {formatFileSize(pdf.fileSize)}</div>
                      )}
                      {pdf.imageCount > 0 && (
                        <div style={styles.pdfSize}>🖼️ {pdf.imageCount} image{pdf.imageCount !== 1 ? "s" : ""}</div>
                      )}
                    </div>
                  </div>
                </div>

                {pdf.description && (
                  <div style={styles.pdfDescription} title={pdf.description}>
                    {pdf.description}
                  </div>
                )}

                <div style={styles.pdfActions}>
                  <button
                    onClick={() => handleViewPdf(pdf)}
                    style={{ ...styles.actionButton, ...styles.viewButton }}
                    title="View PDF"
                  >
                    👁️ View
                  </button>
                  <button
                    onClick={() => openWhatsappModal(pdf)}
                    style={{ ...styles.actionButton, backgroundColor: "#25d366", color: "#fff" }}
                    title="Send to WhatsApp"
                  >
                    📱 WhatsApp
                  </button>
                  <PermissionGate resource="contacts" action="delete">
                    <button
                      onClick={() => handleDeleteClick(pdf._id)}
                      style={{ ...styles.actionButton, ...styles.deleteButton }}
                      title="Delete PDF"
                    >
                      🗑️ Delete
                    </button>
                  </PermissionGate>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Send to WhatsApp Modal */}
      {whatsappModal.open && whatsappModal.pdf && (
        <div style={styles.modalOverlay} onClick={closeWhatsappModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Send PDF to WhatsApp</h2>
            <p style={{ ...styles.modalMessage, marginBottom: "12px" }}>
              {whatsappModal.pdf.fileName || "PDF"} will be sent.
            </p>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "6px", fontWeight: 500 }}>Phone (10 digits)</label>
              <input
                type="text"
                value={whatsappModal.phone}
                onChange={(e) =>
                  setWhatsappModal((prev) => ({ ...prev, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))
                }
                placeholder="9876543210"
                maxLength={10}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #e9edef",
                  borderRadius: "6px",
                  fontSize: "16px",
                }}
              />
            </div>
            <div style={styles.modalActions}>
              <button onClick={closeWhatsappModal} style={{ ...styles.modalButton, ...styles.modalCancelButton }}>
                Cancel
              </button>
              <button
                onClick={handleSendWhatsAppPdf}
                disabled={sendingWhatsApp || whatsappModal.phone.replace(/\D/g, "").length !== 10}
                style={{ ...styles.modalButton, backgroundColor: "#25d366", color: "#fff" }}
              >
                {sendingWhatsApp ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div style={styles.modalOverlay} onClick={handleDeleteCancel}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Delete PDF</h2>
            <p style={styles.modalMessage}>
              Are you sure you want to delete this PDF? This action cannot be undone.
            </p>
            <div style={styles.modalActions}>
              <button
                onClick={handleDeleteCancel}
                style={{ ...styles.modalButton, ...styles.modalCancelButton }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{ ...styles.modalButton, ...styles.modalConfirmButton }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal (same as bills: view always shows, error or PDF) */}
      {pdfPreviewModal.open && pdfPreviewModal.pdf && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            zIndex: 1003,
          }}
          onClick={closePdfPreviewModal}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              maxWidth: "95vw",
              maxHeight: "95vh",
              width: "900px",
              height: "90vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid #e9edef",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: "16px" }}>
                {pdfPreviewModal.pdf?.fileName || "PDF"}
              </span>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {pdfPreviewModal.blob && (
                  <button
                    type="button"
                    onClick={handleDownloadPdfFromModal}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#198754",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    Download PDF
                  </button>
                )}
                {pdfPreviewModal.blobUrl && (
                  <button
                    type="button"
                    onClick={handleOpenPdfInNewTab}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#0d6efd",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    Open PDF in new tab
                  </button>
                )}
                <button
                  type="button"
                  onClick={closePdfPreviewModal}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#6c757d",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              {pdfPreviewModal.errorMessage ? (
                <div
                  style={{
                    padding: "24px",
                    textAlign: "center",
                    color: "#667781",
                    fontSize: "16px",
                  }}
                >
                  {pdfPreviewModal.errorMessage}
                </div>
              ) : pdfPreviewModal.blobUrl ? (
                <iframe
                  title="PDF preview"
                  src={pdfPreviewModal.blobUrl}
                  style={{ flex: 1, width: "100%", border: "none" }}
                />
              ) : (
                <div style={{ padding: "24px", textAlign: "center", color: "#8696a0" }}>
                  Loading…
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navbar */}
      <BottomNavbar />
    </div>
  );
};

export default PDFManagement;
